// /app/frontend/src/pages/account/SecurityPage.jsx
// User MFA management — self-enrollment when optional, setup when required
import { useState } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Shield, AlertTriangle, LogOut, ShieldCheck, Lock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import MFASetup from '../../components/security/MFASetup';
import { useAuth } from '../../contexts/AuthContext';

export default function SecurityPage() {
  const { user, mfaEnforcement, logout } = useAuth();
  const mustSetup = mfaEnforcement?.must_setup;
  const mfaEnabled = mfaEnforcement?.mfa_enabled;
  const [setupEnabled, setSetupEnabled] = useState(false);

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="h-6 w-6 text-violet-400" />
            Security Settings
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Manage your account security and two-factor authentication
          </p>
        </div>

        {/* MFA Enforcement Banner — shown when admin requires MFA and user hasn't set it up */}
        {mustSetup && (
          <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 space-y-3" data-testid="mfa-enforcement-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-amber-300 font-medium">MFA Required</p>
                <p className="text-amber-400/80 text-sm mt-1">
                  Your administrator requires you to enable two-factor authentication.
                  Please set up MFA below to continue using the platform.
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={logout} className="text-slate-400 border-slate-600 hover:bg-slate-800" data-testid="mfa-enforcement-logout-btn">
              <LogOut className="h-4 w-4 mr-2" />
              Log Out (come back later)
            </Button>
          </div>
        )}

        {/* Account Info */}
        <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-800 text-sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-violet-600/20 border border-violet-500/30 flex items-center justify-center text-violet-400 font-semibold">
              {user?.full_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase()}
            </div>
            <div>
              <p className="text-white font-medium">{user?.full_name}</p>
              <p className="text-slate-400">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* MFA Status Card */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {mfaEnabled
                  ? <ShieldCheck className="h-5 w-5 text-emerald-400" />
                  : <Shield className="h-5 w-5 text-slate-400" />
                }
                <div>
                  <CardTitle className="text-white text-base">Two-Factor Authentication</CardTitle>
                  <CardDescription>TOTP-based (Google Authenticator, Microsoft Authenticator, Authy)</CardDescription>
                </div>
              </div>
              <Badge variant="outline" className={mfaEnabled
                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                : 'bg-slate-500/10 text-slate-400 border-slate-500/20'
              } data-testid="mfa-status-badge">
                {mfaEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {mfaEnabled ? (
              <>
                <p className="text-sm text-slate-400">
                  MFA is active on your account. Contact your administrator if you need to reset it.
                </p>
              </>
            ) : mustSetup ? (
              /* Show setup flow when admin requires it */
              <MFASetup />
            ) : setupEnabled ? (
              /* Show setup flow when user self-enrolls */
              <MFASetup />
            ) : (
              <>
                <p className="text-sm text-slate-400">
                  MFA is not enabled on your account. Enhance your account security by enabling two-factor authentication.
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  Note: Your administrator may have disabled MFA for your organization, in which case you won't be able to enable it.
                </p>
                <Button
                  onClick={() => setSetupEnabled(true)}
                  variant="default"
                  className="bg-violet-600 hover:bg-violet-700 text-white mt-3"
                >
                  <Lock className="h-4 w-4 mr-2" />
                  Enable MFA
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
